import { DateOnly } from '@ag2-weathermaker/shared/helper/date-only';
import { dateStringToUtcNoonEpoch, todayAsDateString } from '@ag2-weathermaker/shared/helper/date-time-utils';
import { defaultPageView, SORT_ORDER_ID_FIELD } from '@ag2-weathermaker/shared/types/constants';
import {
  DailyPeriod,
  DataRegion,
  ForecastMode,
  LocalStorageKeys,
  Parameters,
  SubseasonalView,
} from '@ag2-weathermaker/shared/types/enums';
import { ImportedSourcePayload } from '@ag2-weathermaker/shared/types/forecast-source-import.types';
import {
  Day,
  ForecastSource,
  ManualEditConfiguration,
  PageViews,
  ParameterConfig,
  Period,
  SiteCoordinatesMap,
  TerraDrawPolygonProperties,
  WeightingConfiguration,
  WeightingConfigurationWithValues,
} from '@ag2-weathermaker/shared/types/main-content.types';
import { GraphicsSettings, WeathermakerForecastTypes } from '@ag2-weathermaker/shared/types/publishing-center.types';
import { inject, Injectable } from '@angular/core';
import { Feature, FeatureCollection, GeoJSON, GeoJsonProperties, Geometry } from 'geojson';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from '../local-storage.service';

@Injectable({
  providedIn: 'root',
})

/*This service will be used to maintain state of various data structures throughout the application */
export class DataService {
  private locStorage = inject(LocalStorageService);

  public paramConfigSettings$ = new BehaviorSubject<ParameterConfig>(this.getDefaultParameterConfig());
  public isEverythingLoaded$ = new BehaviorSubject<boolean>(false);
  public activePage$ = new BehaviorSubject<PageViews>(defaultPageView);
  private ec46InitTime$ = new BehaviorSubject<number | null>(null);
  private selectedSubRegionFilter$ = new BehaviorSubject<string | null>(null);
  private selectedCustomFilter$ = new BehaviorSubject<{ id: number; name: string } | null>(null);
  private customFilterSiteOrder$ = new BehaviorSubject<{ site_id: string; list_order: number | null }[]>([]);
  private selectedTableSort$ = new BehaviorSubject<string>(`${SORT_ORDER_ID_FIELD}:asc`);
  private editableSiteList$ = new BehaviorSubject<string[]>([]);
  private siteCoordinates$ = new BehaviorSubject<SiteCoordinatesMap>(new Map());
  private terraDrawFeatures$ = new BehaviorSubject<FeatureCollection>({
    type: 'FeatureCollection',
    features: [],
  });

  /** contains all weighting configurations in a single array
   * weighting configurations can be "grouped" to a polygon by evaluating the featureId
   */
  private weightingConfiguation$ = new BehaviorSubject<WeightingConfigurationWithValues[]>([]);

  private weightedAnomalyContour$ = new BehaviorSubject<FeatureCollection | null>(null);

  private editFeatureCollection$ = new BehaviorSubject<FeatureCollection | null>(null);
  private validPeriods$ = new BehaviorSubject<Period[]>([]);
  private baselineChange$ = new BehaviorSubject<string>('');
  private existingForecastSources$ = new BehaviorSubject<ForecastSource[]>([]);
  private latestForecastForEachSource$ = new BehaviorSubject<ForecastSource[]>([]);
  private importedForecastSourceCandidates$ = new BehaviorSubject<ImportedSourcePayload[]>([]);

  private tableNames$ = new BehaviorSubject<string[]>([]);

  private currentWeightingConfiguration$ = new BehaviorSubject<WeightingConfiguration[]>([]);

  private workingFcstDataChanged$ = new BehaviorSubject<void>(undefined);

  private validDays$ = new BehaviorSubject<Day[]>([]);

  private mapDeletedId$ = new BehaviorSubject<string>('');

  private defaultRegion$ = new BehaviorSubject<DataRegion>(DataRegion.NA);
  private superSecretMode$ = new BehaviorSubject<boolean>(false);

  // Which forecast range the user is in (persisted so it survives reload/reset), and, when in
  // subseasonal, which sub-view (daily/weekly/period) — records the "forecast range mode" (WXMK-1377).
  private forecastMode$ = new BehaviorSubject<ForecastMode>(
    this.locStorage.getItem<ForecastMode>(LocalStorageKeys.forecastMode) ?? ForecastMode.ShortRange
  );
  private subseasonalView$ = new BehaviorSubject<SubseasonalView>(
    this.locStorage.getItem<SubseasonalView>(LocalStorageKeys.subseasonalView) ?? SubseasonalView.Weekly
  );
  // Gates the internal-only subseasonal toggle. Set from the /allowed response in AuthGuard —
  // currently mocked to `true` until internal users are marked (see WXMK-1370).
  private isInternalUser$ = new BehaviorSubject<boolean>(false);

  //getters
  getTerraDrawFeatures(): Observable<FeatureCollection> {
    return this.terraDrawFeatures$.asObservable();
  }

  getTerraDrawFeaturesValue(): FeatureCollection {
    return this.terraDrawFeatures$.value;
  }

  getWorkingFcstDataChanged(): Observable<void> {
    return this.workingFcstDataChanged$.asObservable();
  }

  getParamsSettings(): Observable<ParameterConfig> {
    return this.paramConfigSettings$.asObservable();
  }

  getParamsSettingsValue(): ParameterConfig {
    return this.paramConfigSettings$.value;
  }

  getSiteCoordinatesValue(): SiteCoordinatesMap {
    return this.siteCoordinates$.value;
  }

  getEditableSiteListValue(): string[] {
    return this.editableSiteList$.value;
  }

  getDefaultRegion(): Observable<DataRegion> {
    return this.defaultRegion$.asObservable();
  }

  getDefaultRegionValue(): DataRegion {
    return this.defaultRegion$.value;
  }

  getSuperSecretModeValue(): boolean {
    return this.superSecretMode$.value;
  }

  getForecastMode(): Observable<ForecastMode> {
    return this.forecastMode$.asObservable();
  }

  getForecastModeValue(): ForecastMode {
    return this.forecastMode$.value;
  }

  getSubseasonalView(): Observable<SubseasonalView> {
    return this.subseasonalView$.asObservable();
  }

  getSubseasonalViewValue(): SubseasonalView {
    return this.subseasonalView$.value;
  }

  getIsInternalUser(): Observable<boolean> {
    return this.isInternalUser$.asObservable();
  }

  getIsInternalUserValue(): boolean {
    return this.isInternalUser$.value;
  }

  getMapDeleted(): Observable<string> {
    return this.mapDeletedId$.asObservable();
  }

  getEverythingLoadedStatus(): Observable<boolean> {
    return this.isEverythingLoaded$.asObservable();
  }

  getEverythingLoadedStatusValue(): boolean {
    return this.isEverythingLoaded$.value;
  }

  getBaselineChange(): Observable<string> {
    return this.baselineChange$.asObservable();
  }

  getWeightedAnomalyContour(): Observable<FeatureCollection | null> {
    return this.weightedAnomalyContour$.asObservable();
  }

  getEc46InitTime(): Observable<number | null> {
    return this.ec46InitTime$.asObservable();
  }

  getEc46InitTimeValue(): number | null {
    return this.ec46InitTime$.value;
  }

  getWeightingConfigurationWithValuesValue(): WeightingConfigurationWithValues[] {
    return this.weightingConfiguation$.value;
  }

  getExistingForecastSources(): Observable<ForecastSource[]> {
    return this.existingForecastSources$.asObservable();
  }

  getExistingForecastSourcesValue(): ForecastSource[] {
    return this.existingForecastSources$.value;
  }

  getLatestForecastForEachSource(): Observable<ForecastSource[]> {
    return this.latestForecastForEachSource$.asObservable();
  }

  getImportedForecastSourceCandidatesValue(): ImportedSourcePayload[] {
    return this.importedForecastSourceCandidates$.value;
  }

  getTableNamesValue(): string[] {
    return this.tableNames$.value;
  }

  getSelectedSubRegionFilter(): Observable<string | null> {
    return this.selectedSubRegionFilter$.asObservable();
  }

  getSelectedSubRegionFilterValue(): string | null {
    return this.selectedSubRegionFilter$.value;
  }

  getSelectedCustomFilter(): Observable<{ id: number; name: string } | null> {
    return this.selectedCustomFilter$.asObservable();
  }

  getSelectedCustomFilterValue(): { id: number; name: string } | null {
    return this.selectedCustomFilter$.value;
  }

  getCustomFilterSiteOrder(): Observable<{ site_id: string; list_order: number | null }[]> {
    return this.customFilterSiteOrder$.asObservable();
  }

  getSelectedTableSort(): Observable<string> {
    return this.selectedTableSort$.asObservable();
  }

  getSelectedTableSortValue(): string {
    return this.selectedTableSort$.value;
  }

  getCurrentWeightingConfiguration(): Observable<WeightingConfiguration[]> {
    return this.currentWeightingConfiguration$.asObservable();
  }

  getCurrentWeightingConfigurationValue(): WeightingConfiguration[] {
    return this.currentWeightingConfiguration$.value;
  }

  isImportedBaselineMissing(): boolean {
    if (!this.superSecretMode$.getValue()) {
      return false;
    }

    if (!this.locStorage.getItem<boolean>(LocalStorageKeys.selectedBaselineIsImported)) {
      return false;
    }

    const sources = this.existingForecastSources$.getValue();
    return !sources.some((source) => source.isBaseLine);
  }

  /*SETTERS*/
  setTerraDrawFeatures(featureCollection: FeatureCollection): void {
    const normalized = (featureCollection.features ?? []).flat(); // flatten one level
    this.terraDrawFeatures$.next({
      type: 'FeatureCollection',
      features: normalized,
    });
  }

  setEc46InitTime(initTimeMs: number): void {
    this.ec46InitTime$.next(initTimeMs);
  }

  setEverythingLoadedStatus(loaded: boolean): void {
    this.isEverythingLoaded$.next(loaded);
  }

  setDefaultRegion(region: DataRegion): void {
    this.defaultRegion$.next(region);
    this.locStorage.setItem(LocalStorageKeys.defaultRegion, region);
  }

  setSuperSecretMode(superSecretMode: boolean): void {
    this.superSecretMode$.next(superSecretMode);
  }

  setForecastMode(mode: ForecastMode): void {
    this.forecastMode$.next(mode);
    this.locStorage.setItem(LocalStorageKeys.forecastMode, mode);
  }

  setSubseasonalView(view: SubseasonalView): void {
    this.subseasonalView$.next(view);
    this.locStorage.setItem(LocalStorageKeys.subseasonalView, view);
  }

  setIsInternalUser(isInternalUser: boolean): void {
    this.isInternalUser$.next(isInternalUser);
  }

  setBaselineChange(baselineId: string): void {
    this.baselineChange$.next(baselineId);
  }

  setMapDeleted(mapId: string): void {
    this.mapDeletedId$.next(mapId);
  }

  setWorkingFcstDataChanged(): void {
    this.workingFcstDataChanged$.next();
  }

  setSiteCoordinates(coordinates: SiteCoordinatesMap): void {
    this.siteCoordinates$.next(coordinates);
  }

  setParamSettings(updatedSettings: ParameterConfig | null, save = true) {
    updatedSettings = updatedSettings ?? this.getDefaultParameterConfig();
    this.paramConfigSettings$.next(updatedSettings);
    if (save) {
      this.locStorage.setItem(LocalStorageKeys.params, updatedSettings);
    }
  }

  setActivePage(activePage: PageViews, save = true) {
    this.activePage$.next(activePage);
    if (save) {
      this.locStorage.setItem(LocalStorageKeys.activePage, JSON.parse(JSON.stringify(activePage)));
    }
  }

  setSelectedSubRegionFilter(selectedSubRegionFilter: string | null, save = true): void {
    this.selectedSubRegionFilter$.next(selectedSubRegionFilter);
    if (save) {
      this.locStorage.setItem(LocalStorageKeys.selectedSubRegionFilter, selectedSubRegionFilter);
    }
  }

  setSelectedCustomFilter(filter: { id: number; name: string } | null, save = true): void {
    this.selectedCustomFilter$.next(filter);
    if (!filter && this.selectedTableSort$.value.startsWith('listOrder')) {
      this.setSelectedTableSort(`${SORT_ORDER_ID_FIELD}:asc`);
    }

    if (save) {
      this.locStorage.setItem(LocalStorageKeys.selectedCustomFilterId, filter?.id ?? null);
    }
  }

  setCustomFilterSiteOrder(sites: { site_id: string; list_order: number | null }[]): void {
    this.customFilterSiteOrder$.next(sites);
  }

  setSelectedTableSort(value: string): void {
    this.selectedTableSort$.next(value);
    this.locStorage.setItem(LocalStorageKeys.selectedTableSort, value);
  }

  updateParamSettings(partial: Partial<ParameterConfig>, save = true) {
    const updated = { ...this.paramConfigSettings$.value, ...partial };
    this.paramConfigSettings$.next(updated);
    if (save) {
      this.locStorage.setItem(LocalStorageKeys.params, JSON.parse(JSON.stringify(updated)));
    }
  }

  setEditFeatureCollection(editFeatureCollection: FeatureCollection | null): void {
    this.editFeatureCollection$.next(editFeatureCollection);
    this.locStorage.setItem(LocalStorageKeys.editConfigurationWithValues, editFeatureCollection);
  }

  setEditableSiteList(sites: string[]): void {
    this.editableSiteList$.next(sites);
  }

  setValidPeriods(periods: Period[]): void {
    this.validPeriods$.next(periods);
  }

  setTableNames(tableNames: string[]): void {
    this.tableNames$.next(tableNames);
    this.locStorage.setItem(LocalStorageKeys.tableNames, this.getPersistableTableNames(tableNames));
  }

  private getPersistableTableNames(tableNames: string[]): string[] {
    if (!this.superSecretMode$.getValue()) {
      return tableNames;
    }

    const importedSourceIds = new Set(
      this.existingForecastSources$
        .getValue()
        .filter((source) => source.type === WeathermakerForecastTypes.WM_IMPORTED_SOURCE)
        .map((source) => source.id)
    );

    return tableNames.filter((tableName) => !importedSourceIds.has(tableName));
  }

  setWeightedAnomalyContour(contour: FeatureCollection | null): void {
    this.weightedAnomalyContour$.next(contour);
    this.locStorage.setItem(LocalStorageKeys.weightedAnomalyContours, contour);
  }

  setValidDays(days: Day[]): void {
    this.validDays$.next(days);
  }

  setCurrentWeightingConfiguration(weightingConfiguration: WeightingConfiguration[]): void {
    this.currentWeightingConfiguration$.next(weightingConfiguration);
    this.locStorage.setItem(LocalStorageKeys.weightingConfiguration, weightingConfiguration);
  }

  replaceWeightingConfigurations(
    sourceParameter: Parameters,
    sourceDayValue: DateOnly,
    targetDayValue: DateOnly,
    oldToNewFeatureIdsMapping: Record<string, string>,
    targetParameter?: Parameters
  ) {
    const effectiveTargetParam = targetParameter ?? sourceParameter;
    const configurations = this.getCurrentWeightingConfigurationValue();
    const sourceDayConfigs = this.filterWeightingConfigs(configurations, sourceParameter, sourceDayValue);

    if (sourceDayConfigs.length === 0) {
      return;
    }

    const clonedData = JSON.parse(JSON.stringify(sourceDayConfigs));
    const newTargetConfigs = (clonedData as WeightingConfiguration[]).map((config) => {
      config.date = targetDayValue.value;
      config.param = effectiveTargetParam;
      config.featureId = oldToNewFeatureIdsMapping[config.featureId];

      return config;
    });

    const configurationsWithoutTargetDay = configurations.filter(
      (config) => !(config.param === effectiveTargetParam && config.date === targetDayValue.value)
    );

    const updatedConfigurations = [...configurationsWithoutTargetDay, ...newTargetConfigs];

    this.setCurrentWeightingConfiguration(updatedConfigurations);

    return newTargetConfigs;
  }

  filterWeightingConfigs(
    configurations: WeightingConfiguration[],
    parameter: Parameters,
    dateValue: DateOnly
  ): WeightingConfiguration[] {
    return configurations.filter((config) => config.param === parameter && config.date === dateValue.value);
  }

  //non-getters or setters
  setExistingForecastSources(sources: ForecastSource[]): void {
    this.existingForecastSources$.next(sources);

    const sourcesToPersist = this.superSecretMode$.getValue() ? this.getForecastSourcesForSecretMode(sources) : sources;

    const forStorage = sourcesToPersist.map(
      ({ features: _features, anomalyFeatures: _anomalyFeatures, ...rest }) => rest
    );
    this.locStorage.setItem(LocalStorageKeys.forecastSources, forStorage);
  }

  private getForecastSourcesForSecretMode(sources: ForecastSource[]): ForecastSource[] {
    const baseline = sources.find((source) => source.isBaseLine);
    if (baseline) {
      const isImportedBaseline = baseline.type === WeathermakerForecastTypes.WM_IMPORTED_SOURCE;
      this.locStorage.setItem(LocalStorageKeys.selectedBaselineIsImported, isImportedBaseline);
    }

    return sources.filter((source) => source.type !== WeathermakerForecastTypes.WM_IMPORTED_SOURCE);
  }

  setImportedForecastSourceCandidates(): void {
    let persisted: ImportedSourcePayload[] = [];
    if (!this.superSecretMode$.getValue()) {
      persisted = this.locStorage.getItem<ImportedSourcePayload[]>(LocalStorageKeys.importedSources) ?? [];
    }

    this.importedForecastSourceCandidates$.next(persisted);
  }

  getImportedPayloadsForSources(sources: ForecastSource[]): ImportedSourcePayload[] {
    const wantedIds = new Set(sources.map((source) => source.id));

    return this.importedForecastSourceCandidates$.getValue().filter((candidate) => wantedIds.has(candidate.source.id));
  }

  addImportedForecastSourceCandidate(payload: ImportedSourcePayload): void {
    const incomingIds = new Set([payload.source.id]);
    const existing = this.importedForecastSourceCandidates$
      .getValue()
      .filter((candidate) => !incomingIds.has(candidate.source.id));
    const importCandidates = [...existing, payload];

    this.importedForecastSourceCandidates$.next(importCandidates);

    if (!this.superSecretMode$.getValue()) {
      this.locStorage.setItem(LocalStorageKeys.importedSources, importCandidates);
    }
  }

  setLatestForecastForEachSource(availableForecastSources: ForecastSource[]): void {
    const latestForecastForEachSource = Object.values(
      availableForecastSources.reduce(
        (acc, item) => {
          if (!acc[item.type] || item.init_time > acc[item.type]!.init_time) {
            acc[item.type] = item;
          }

          return acc;
        },
        {} as Record<string, ForecastSource>
      )
    );

    this.latestForecastForEachSource$.next(latestForecastForEachSource);
  }

  updateOneContourAnomaly(key: string, contourAnomaly: GeoJSON, type: 'period' | 'day') {
    //determine which list to find time period to change
    const listOfDayOrPeriod = type === 'period' ? this.validPeriods$.getValue() : this.validDays$.getValue();

    //find index of the period or day to change
    const foundIndex = listOfDayOrPeriod.findIndex((item) => item.key === key); // will be a period or a day

    //setting item to new anomaly contour
    listOfDayOrPeriod[foundIndex].anomalyContour.value = contourAnomaly;

    if (type === 'period') {
      //Okay to cast here because we are only changing the anomalyContour
      // which both period and day have
      this.setValidPeriods(listOfDayOrPeriod as Period[]);
    }

    if (type === 'day') {
      //same here
      this.setValidDays(listOfDayOrPeriod as Day[]);
    }
  }
  //if a table has been removed we want to remove it from
  // the list of existing tables so it can be selected again
  // if user should decide
  removeTableFromRegisterdTableNames(tableId: string) {
    //remove table from the list of tables
    const tables = this.getTableNamesValue();
    const index = tables.indexOf(tableId);
    if (index > -1) {
      tables.splice(index, 1);
      this.setTableNames(tables);
    }
  }

  addFeatures(features: Feature<Geometry, GeoJsonProperties>[]) {
    const collection = this.getTerraDrawFeaturesValue();
    const updated: any = {
      type: 'FeatureCollection',
      features: Object.prototype.hasOwnProperty.call(collection, 'features')
        ? [...collection.features.filter((f) => !features.some((ff) => ff.id === f.id)), ...features].flat()
        : [],
    };
    this.terraDrawFeatures$.next(updated);
  }

  replaceFeatures(
    polygonType: string,
    sourceParameter: Parameters,
    sourceDayValue: DateOnly,
    targetDayValue: DateOnly,
    targetParameter?: Parameters
  ): Record<string, string> {
    const effectiveTargetParam = targetParameter ?? sourceParameter;
    const collection = this.getTerraDrawFeaturesValue();
    const sourceDayFeatures = this.filterFeatures(collection, polygonType, sourceParameter, sourceDayValue);

    if (sourceDayFeatures.length === 0) {
      return {};
    }

    const clonedData = JSON.parse(JSON.stringify(sourceDayFeatures));

    const newTargetDayFeatures = clonedData as Feature<Geometry, TerraDrawPolygonProperties>[];

    const oldToNewFeatureIdsMapping: Record<string, string> = {};

    newTargetDayFeatures.forEach((feature) => {
      const featureId = crypto.randomUUID();
      oldToNewFeatureIdsMapping[feature.id as string] = featureId;
      feature.id = featureId;
      feature.properties.date = targetDayValue.value;
      feature.properties.param = effectiveTargetParam;
      feature.properties.hoverId = featureId;
    });

    const featuresWithoutTargetDay = collection.features.filter((feature) => {
      if (feature.properties === null) {
        return true;
      }

      if (feature.properties['polygonType'] !== polygonType || feature.properties['param'] !== effectiveTargetParam) {
        return true;
      }

      const featureDate = feature.properties['date'];
      return featureDate == null || !DateOnly.resolve(featureDate).equals(targetDayValue);
    });

    const updateCollection = {
      ...collection,
      features: [...featuresWithoutTargetDay, ...newTargetDayFeatures],
    };

    this.terraDrawFeatures$.next(updateCollection);

    localStorage.setItem(LocalStorageKeys.terraDrawFeatures, JSON.stringify(updateCollection));

    return oldToNewFeatureIdsMapping;
  }

  filterFeatures(collection: FeatureCollection, polygonType: string, parameter: Parameters, dateValue: DateOnly) {
    return collection.features.filter((feature) => {
      if (feature.properties === null) {
        return false;
      }

      const properties = feature.properties as TerraDrawPolygonProperties;
      if (properties.polygonType !== polygonType || properties.param !== parameter || properties.date == null) {
        return false;
      }

      return DateOnly.resolve(properties.date).equals(dateValue);
    });
  }

  saveManualEditConfiguration(manualEditConfiguration: ManualEditConfiguration[]): void {
    this.locStorage.setItem(LocalStorageKeys.manualEditConfiguration, manualEditConfiguration);
  }

  setGraphicsSettings(settings: GraphicsSettings): void {
    this.locStorage.setItem(LocalStorageKeys.graphicsSettings, settings);
  }

  getGraphicsSettings(): GraphicsSettings | null {
    return this.locStorage.getItem<GraphicsSettings>(LocalStorageKeys.graphicsSettings);
  }

  setWeightingConfigurationValues(weightingConfiguration: WeightingConfigurationWithValues[]): void {
    this.weightingConfiguation$.next(weightingConfiguration);
    this.locStorage.setItem(LocalStorageKeys.weightingConfigurationWithValues, weightingConfiguration);
  }

  doesTableExist(tableName: string): boolean {
    const tables = this.getTableNamesValue();
    if (!tables || tables.length === 0) {
      return false;
    }

    return tables.includes(tableName);
  }

  getDefaultParameterConfig(): ParameterConfig {
    const stored = localStorage.getItem(LocalStorageKeys.dayOne);
    const dayOneDate = stored && /^\d{4}-\d{2}-\d{2}$/.test(stored) ? stored : todayAsDateString();
    const epoch = dateStringToUtcNoonEpoch(dayOneDate);

    const [y, m, d] = dayOneDate.split('-').map(Number);
    const utcDate = new Date(Date.UTC(y, m - 1, d));
    const monthShort = utcDate.toLocaleDateString('default', { month: 'short', timeZone: 'UTC' });
    const label = `Day 1 ${d}-${monthShort}`;

    return {
      dailyPeriod: DailyPeriod.daily,
      dayPeriodParameter: 'parameter',
      parameter: 'max' as Parameters,
      timeRange: '1-5day',
      tempDfn: 'temp',
      normals: '30',
      contour: true,
      day: {
        value: dayOneDate,
        day: 1,
        label,
        epoch: epoch,
      },
    };
  }
}
